import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useData } from "@/context/DataContext";
import { PageHeader, Panel, StatusBadge } from "@/components/Widgets";
import { errMsg, fmt, http } from "@/lib/api";
import { toast } from "sonner";

const Icon = ({ s }) => (s === "ok" ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : s === "mismatch" ? <XCircle className="h-4 w-4 text-rose-400" /> : <MinusCircle className="h-4 w-4 text-slate-500" />);

export default function Validation() {
  const { data, versions, filters } = useData();
  const fe = data.model.entities.find((x) => x.code === filters.entity);
  const [vid, setVid] = useState(data.version.id);
  const [res, setRes] = useState(null);
  useEffect(() => {
    http.get(`/versions/${vid}/validation`).then((r) => setRes(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [vid]);
  return (
    <div data-testid="validation-page">
      <PageHeader kicker="Governance" title={fe ? `Data Validation - ${fe.short}` : "Data Validation"}>
        <Select value={vid} onValueChange={setVid}>
          <SelectTrigger className="w-[260px] border-slate-700 bg-slate-900/70" data-testid="validation-version-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#131c2e]">{versions.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>
      <Panel title="Version validation status" testid="validation-versions">
        <div className="flex flex-wrap gap-3">
          {versions.map((v) => (
            <div key={v.id} className="flex items-center gap-2 rounded-lg border border-slate-800 px-3 py-2 text-sm">
              <span className="text-slate-200">{v.name}</span>
              <StatusBadge ok={!!v.validation_ok} testid={`validation-status-${v.id}`}>{v.validation_ok ? "Reconciled" : "Differences"}</StatusBadge>
              <span className="text-[11px] text-slate-500">{v.status}</span>
            </div>
          ))}
        </div>
      </Panel>
      {res && (
        <>
          <Panel className="mt-6" title="Dashboard vs workbook reconciliation" subtitle="Totals over all years; any year with a difference ≥ 0.01 Cr is listed" testid="validation-metrics">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="py-2" /><th className="py-2">Measure</th><th className="py-2">Workbook reference</th><th className="py-2 text-right">Workbook</th><th className="py-2 text-right">Dashboard</th><th className="py-2 text-right">Max diff</th><th className="py-2 pl-4">Years with difference</th>
                </tr></thead>
                <tbody>
                  {res.metrics.map((m) => (
                    <tr key={m.metric} className="border-t border-slate-800" data-testid={`validation-row-${m.metric.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <td className="py-2 pr-2"><Icon s={m.status} /></td>
                      <td className="py-2 text-slate-100">{m.metric}</td>
                      <td className="py-2 text-xs text-slate-500">{m.ref}{m.basis && <span className="ml-1 text-slate-600">({m.basis})</span>}</td>
                      <td className="num py-2 text-right">{fmt(m.workbook_total)}</td>
                      <td className="num py-2 text-right">{fmt(m.dashboard_total)}</td>
                      <td className={`num py-2 text-right ${m.status === "mismatch" ? "text-rose-400" : "text-slate-400"}`}>{m.max_diff ?? "–"}</td>
                      <td className="py-2 pl-4 text-xs text-slate-400">{m.status === "not_found" ? "Not found in workbook" : m.years_with_diff?.join(", ") || "None"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
          <Panel className="mt-6" title="Entity sheet arithmetic checks" subtitle={(() => { const ec = res.entity_checks.filter((c) => !fe || c.entity === fe.short); return `${ec.filter((c) => !c.ok).length} failing of ${ec.length}${fe ? " · group reconciliation above stays group-wide" : ""}`; })()} testid="validation-entity-checks">
            <div className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs md:grid-cols-2">
              {res.entity_checks.filter((c) => !fe || c.entity === fe.short).map((c, i) => (
                <div key={i} className="flex items-center gap-2"><Icon s={c.ok ? "ok" : "mismatch"} /><span className="text-slate-400">{c.entity}</span><span className={c.ok ? "text-slate-300" : "text-rose-300"}>{c.check}</span></div>
              ))}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
